import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Pre-construction (Phase 1, 2026-10-20) — the six-step page. Steps switch
// client-side (the panels are all on the page); every action posts JSON and
// the server answers with the panels it changed plus the page summary, so
// only those parts are swapped. Uploads go straight to S3 (presign → PUT →
// register) with the chip pattern from report_form_controller.
export default class extends Controller {
  static targets = ["panel", "stepButton", "stepCircle", "stepName", "stepSub",
                    "questionsCount", "addendaCount", "budgetTotal", "perSf",
                    "budget", "dropzone", "fileInput", "docChips", "aiBanner", "searchInput", "divisionTable", "noMatch",
                    "editPackagesModal", "modal", "codesList", "codesFilters", "codesGroup", "codesNoMatch",
                    "trow", "datesModal", "remindersModal", "att"]
  static values = { step: { type: Number, default: 1 }, view: { type: String, default: "steps" }, projectId: Number, baseUrl: String, presignUrl: String, registerUrl: String, strings: Object, attention: Object }

  // The "By cost code" view (prototype B) has no step panels: a row per cost
  // code, each with its own steps 1 to 6, and one open cost code's panel.
  get _codesView() { return this.viewValue === "codes" }

  // Translated words for the page's JS (the page passes them in; English fallback).
  _t(key, vars = {}) {
    let s = (this.stringsValue && this.stringsValue[key]) || key
    Object.entries(vars).forEach(([k, v]) => { s = s.replaceAll(`%{${k}}`, v) })
    return s
  }

  connect() {
    const hash = parseInt((window.location.hash || "").replace("#step-", ""), 10)
    if (hash >= 1 && hash <= 6) this.stepValue = hash
    const pkg = new URLSearchParams(window.location.search).get("package_id")
    if (pkg && !this._codesView) this._selectedPackage = pkg
    if (!this._codesView) this.showStep(this.stepValue, false)
    this._tickCountdown()
    this._clock = setInterval(() => this._tickCountdown(), 1000)
    this._watchAiRuns()
    this._keydown = (e) => { if (e.key === "Escape") this._closeOpenModals() }
    document.addEventListener("keydown", this._keydown)
    // Speed: the open step came with the page; load the other five in the
    // background right away, so every step switch is instant (like the prototype).
    if (!this._codesView) this._prefetchTimer = setTimeout(() => this._prefetchPanels(), 50)
    // The View switch (in the breadcrumbs) opens the other view on the same
    // step and package, like the prototype's protoSync.
    // The href is set on hover too, so Turbo's hover prefetch loads the right page.
    this._viewSwitch = (e) => {
      const a = e.target.closest?.("a.protoopt")
      if (!a || a.classList.contains("on")) return
      const p = this._codesView ? (typeof this._syncParams === "function" ? this._syncParams() : {}) : { step: this.stepValue, package_id: this._selectedPackage }
      if (!a.dataset.baseHref) a.dataset.baseHref = a.getAttribute("href")
      const url = new URL(a.dataset.baseHref, window.location.origin)
      Object.entries(p || {}).forEach(([k, v]) => { if (v) url.searchParams.set(k, v) })
      a.setAttribute("href", url.pathname + url.search)
    }
    document.addEventListener("pointerover", this._viewSwitch, true)
    document.addEventListener("click", this._viewSwitch, true)
  }

  disconnect() {
    document.removeEventListener("pointerover", this._viewSwitch, true)
    document.removeEventListener("click", this._viewSwitch, true)
    document.removeEventListener("keydown", this._keydown)
    if (this._clock) clearInterval(this._clock)
    if (this._pollTimer) clearTimeout(this._pollTimer)
    if (this._prefetchTimer) clearTimeout(this._prefetchTimer)
  }

  // ── Speed: every step panel loads once, in the background ─────────────
  // A panel is "0" (not loaded), "loading", "1" (on the page) or "stale"
  // (an action elsewhere changed what it shows — reloaded in the background).
  // Two at a time, nearest steps first, so the server stays free for clicks.
  _prefetchPanels() {
    if (this._codesView || this._prefetching) return
    this._prefetching = true
    const lane = async () => {
      for (;;) {
        const cur = this.stepValue
        const next = this.panelTargets
          .filter(p => p.dataset.loaded === "0" || p.dataset.loaded === "stale")
          .map(p => parseInt(p.dataset.step, 10))
          .sort((a, b) => (Math.abs(a - cur) + (a < cur ? 0.5 : 0)) - (Math.abs(b - cur) + (b < cur ? 0.5 : 0)))[0]
        if (!next) return
        await this._loadPanel(next)
        const p = this.panelTargets.find(x => parseInt(x.dataset.step, 10) === next)
        if (p && (p.dataset.loaded === "0" || p.dataset.loaded === "stale")) return // failed: stop, a click loads it
      }
    }
    Promise.all([lane(), lane()]).finally(() => { this._prefetching = false })
  }

  _loadPanel(step) {
    const panel = this.panelTargets.find(p => parseInt(p.dataset.step, 10) === step)
    if (!panel) return Promise.resolve()
    if (panel._loading) return panel._loading
    const wasHtml = panel.dataset.loaded
    panel.dataset.loaded = "loading"
    const q = this._viewQuery
    panel._loading = this._json("GET", `${this.baseUrlValue}/panel/${step}${q ? "?" + q : ""}`)
      .then(data => {
        // Don't overwrite a panel an action already refreshed while we waited.
        if (panel.dataset.loaded === "loading") this._applyResponse({ panels: data.panels })
      })
      .catch(() => { if (panel.dataset.loaded === "loading") panel.dataset.loaded = wasHtml === "1" ? "1" : "0" })
      .finally(() => { panel._loading = null })
    return panel._loading
  }

  // Panels an action changed but didn't send back: reload them quietly.
  _markStale(steps) {
    (steps || []).forEach(n => {
      const panel = this.panelTargets.find(p => parseInt(p.dataset.step, 10) === parseInt(n, 10))
      if (!panel || panel.dataset.loaded === "0") return
      panel.dataset.loaded = "stale"
      if (parseInt(n, 10) === this.stepValue) this._loadPanel(parseInt(n, 10))
    })
    if (this._staleTimer) clearTimeout(this._staleTimer)
    this._staleTimer = setTimeout(() => this._prefetchPanels(), 300)
  }

  // ── Steps ─────────────────────────────────────────────────────────────
  goToStep(event) {
    const step = parseInt(event.currentTarget.dataset.step, 10)
    if (this._codesView) { window.location.href = `${this.baseUrlValue}?step=${step}`; return }
    this.showStep(step, true)
  }

  showStep(step, tracked) {
    this.stepValue = step
    this.panelTargets.forEach(p => {
      const on = parseInt(p.dataset.step, 10) === step
      p.classList.toggle("hidden", !on)
      p.style.display = on ? "flex" : "none"
    })
    this._paintStepBar()
    this._renderAttention()
    history.replaceState(null, "", `#step-${step}`)
    window.scrollTo({ top: 0 })
    // Normally already loaded in the background; if not (or stale), load it now.
    const panel = this.panelTargets.find(p => parseInt(p.dataset.step, 10) === step)
    if (panel && (panel.dataset.loaded === "0" || panel.dataset.loaded === "stale")) this._loadPanel(step)
    document.dispatchEvent(new CustomEvent("precon:step-shown", { detail: { step } }))
    if (tracked) track("Precon Step Opened", { project_id: this.projectIdValue, step })
  }

  // The prototype's step bar: the current step gets the orange ring, done
  // steps an orange check, and the line after a done step turns orange.
  _paintStepBar() {
    const checkSvg = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m4.5 12.75 6 6 9-13.5"/></svg>`
    this.stepButtonTargets.forEach(el => {
      const n = parseInt(el.dataset.step, 10)
      const cur = n === this.stepValue
      const done = el.dataset.done === "1" && !cur
      el.classList.toggle("cur", cur)
      el.classList.toggle("done", done)
      const circle = el.querySelector("[data-precon-target='stepCircle']")
      const alert = !!this._stepAlerts[n]
      if (circle) circle.innerHTML = (done ? checkSvg : String(n)) + (alert ? `<span class="salert" aria-hidden="true">!</span>` : "")
      el.querySelector("[data-precon-target='stepSub']")?.classList.toggle("alert", alert)
      const line = el.querySelector("[data-precon-target='stepLine']")
      if (line) line.classList.toggle("on", el.dataset.done === "1")
    })
  }

  // Open addendum to-dos per step ({ step: count }), from the summary.
  get _stepAlerts() {
    if (this._alerts) return this._alerts
    const a = {}
    Object.entries(this.attentionValue?.steps || {}).forEach(([n, items]) => { const open = items.filter(i => !i.done).length; if (open) a[n] = open })
    return a
  }

  // The prototype's yellow "Addendum 02 changed this step" panel (green "All
  // set here" once every to-do on the step is done; Hide puts it away).
  _renderAttention() {
    if (!this.hasAttTarget || this._codesView) return
    const box = this.attTarget
    box.innerHTML = ""
    const data = this.attentionValue || {}
    const items = (data.steps || {})[String(this.stepValue)] || []
    this._attHidden ||= {}
    if (!items.length || this._attHidden[this.stepValue]) return
    const open = items.filter(i => !i.done).length
    const label = data.label || ""
    const check = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m4.5 12.75 6 6 9-13.5"/></svg>`
    const el = document.createElement("div")
    el.className = `att ${open ? "" : "ok"}`
    el.setAttribute("role", "region")
    const title = open ? this._t("att_changed", { label }) : this._t("att_all_set")
    el.setAttribute("aria-label", title)
    const sub = open ? (open === 1 ? this._t("att_todo_one") : this._t("att_todo_many", { count: open })) : this._t("att_done_sub", { label })
    el.innerHTML = `<div class="att-h"><span class="att-ic">${open ? "!" : check}</span><div class="grow"><b>${this._esc(title)}</b><div class="att-sub">${this._esc(sub)}</div></div>` +
      `<button class="linkbtn" type="button" data-action="click->precon#openAddenda">${this._esc(this._t("att_see", { label }))}</button>` +
      (open ? "" : `<button class="btn btn-secondary btn-sm" type="button" data-att-hide>${this._esc(this._t("att_hide"))}</button>`) + `</div>` +
      items.map(i => `<div class="att-item ${i.done ? "done" : ""}"><div class="grow"><div class="att-t">${this._esc(i.text)}</div>${i.sub ? `<div class="muted">${this._esc(i.sub)}</div>` : ""}</div>` +
        (i.done ? `<span class="att-done">${check}${this._esc(this._t("att_done"))}</span>` : "") + `</div>`).join("")
    el.querySelector("[data-att-hide]")?.addEventListener("click", () => { this._attHidden[this.stepValue] = true; this._renderAttention() })
    box.appendChild(el)
  }

  // Live countdown to the sub bids due date (the prototype's dline).
  _tickCountdown() {
    const el = this.element.querySelector("[data-precon-target='dline']")
    if (!el) return
    const due = el.dataset.due ? new Date(el.dataset.due) : null
    const label = el.querySelector("[data-precon-target='dlineLabel']")
    const value = el.querySelector("[data-precon-target='dlineValue']")
    if (!due || isNaN(due)) return
    // The prototype's countdown: "21d 04h 05m 09s"; once past, "22h 17m ago".
    const ms = due - Date.now()
    const abs = Math.abs(ms)
    const d = Math.floor(abs / 86400000), h = Math.floor(abs % 86400000 / 3600000), m = Math.floor(abs % 3600000 / 60000), s = Math.floor(abs % 60000 / 1000)
    const p2 = (x) => (x < 10 ? "0" : "") + x
    const text = `${d ? `${d}d ` : ""}${p2(h)}h ${p2(m)}m ${p2(s)}s`
    el.classList.remove("closed", "urgent", "soon")
    if (ms <= 0) {
      el.classList.add("closed")
      if (label) label.textContent = el.dataset.labelClosed
      if (value) value.textContent = el.dataset.labelAgo.replace("%{time}", `${d ? `${d}d ` : ""}${d || h ? `${h}h ` : ""}${m}m`)
    } else {
      if (ms < 86400000) el.classList.add("urgent"); else if (ms < 172800000) el.classList.add("soon")
      if (label) label.textContent = el.dataset.labelDue
      if (value) value.textContent = text
    }
  }

  // ── Top row: Important dates + Calendar reminders (the prototype's pop-ups) ──
  openDates() {
    if (this.hasDatesModalTarget) this.datesModalTarget.classList.remove("hidden")
    track("Precon Dates Opened", { project_id: this.projectIdValue })
  }

  // Any prototype pop-up: the backdrop, the ×, Cancel and Done close it.
  closeModal(e) {
    const overlay = e?.currentTarget?.closest(".overlay") || e?.currentTarget
    if (overlay) overlay.classList.add("hidden")
  }

  // A date saves as soon as it changes; the top row shows it at once.
  async changeKeyDate(e) {
    const field = e.currentTarget.dataset.field
    const value = e.currentTarget.value
    const input = e.currentTarget
    if ((input.dataset.was || "") === value) return
    input.dataset.was = value
    // Optimistic: the top row shows the new date right away; the server's row replaces it.
    const row = this.hasTrowTarget ? Array.from(this.trowTarget.querySelectorAll(".kdc-row"))[["site_walk_at", "questions_due_at", "bids_due_at", "owner_bid_due_at"].indexOf(field)] : null
    const dt = value ? new Date(value.replace(" ", "T")) : null
    if (row && dt && !isNaN(dt)) {
      const lang = document.documentElement.lang || "en"
      const mon = new Intl.DateTimeFormat(lang, { month: "short" }).format(dt)
      const time = new Intl.DateTimeFormat(lang, { hour: "numeric", minute: "2-digit" }).format(dt)
      row.querySelector(".kdc-v").textContent = `${mon} ${dt.getDate()} · ${time}`
    }
    if (field === "bids_due_at") {
      const dline = this.element.querySelector("[data-precon-target='dline']")
      if (dline && dt && !isNaN(dt)) { dline.dataset.due = dt.toISOString(); this._tickCountdown() }
    }
    try {
      const data = await this._json("PATCH", `${this.baseUrlValue}/important_dates`, { [field]: value })
      this._applyTop(data)
      this._showToast(this._t("date_saved"))
    } catch (err) {
      this._showToast(err.message, "error")
    }
  }

  // The top row (dates, countdown, counts, cart) comes back whole after a date or reminder change.
  _applyTop(data) {
    if (data.trow && this.hasTrowTarget) {
      const tmp = document.createElement("div")
      tmp.innerHTML = data.trow.trim()
      if (tmp.firstElementChild) this.trowTarget.replaceWith(tmp.firstElementChild)
    }
    if (data.summary) this._applySummary(data.summary)
    if (data.trow) this._applyTop({ trow: data.trow })
    if (data.stale) this._markStale(data.stale)
    if (data.reminders_modal && this.hasRemindersModalTarget) {
      const tmp = document.createElement("div")
      tmp.innerHTML = data.reminders_modal.trim()
      if (tmp.firstElementChild) this.remindersModalTarget.replaceWith(tmp.firstElementChild)
    }
    this._tickCountdown()
  }

  openReminders() {
    const m = this.hasRemindersModalTarget ? this.remindersModalTarget : null
    if (!m) return
    // A fresh draft from what's saved (Cancel throws the draft away).
    this._remDraft = { lead: m.dataset.lead || "1d", people: JSON.parse(m.dataset.people || "[]") }
    m.querySelectorAll("[data-rem-date]").forEach(cb => { cb.checked = cb.defaultChecked })
    m.querySelector("[data-rem-email]").value = ""
    this._paintReminders()
    m.classList.remove("hidden")
    track("Precon Calendar Reminders Opened", { project_id: this.projectIdValue })
  }

  reminderDraftChanged() { this._paintReminders() }

  reminderLead(e) { this._remDraft.lead = e.currentTarget.dataset.lead; this._paintReminders() }

  reminderEmailKey(e) { if (e.key === "Enter") { e.preventDefault(); this.reminderAddPerson() } }

  reminderAddPerson() {
    const m = this.remindersModalTarget
    const input = m.querySelector("[data-rem-email]")
    const em = input.value.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) { this._showToast(m.dataset.labelBadEmail, "error"); return }
    if (!this._remDraft.people.includes(em)) this._remDraft.people.push(em)
    input.value = ""
    this._paintReminders()
  }

  _paintReminders() {
    const m = this.remindersModalTarget
    const d = this._remDraft
    m.querySelectorAll("[data-rem-leads] .fchip").forEach(b => b.classList.toggle("on", b.dataset.lead === d.lead))
    const box = m.querySelector("[data-rem-people]")
    box.querySelectorAll(".rempill:not(.me)").forEach(p => p.remove())
    const input = box.querySelector("[data-rem-email]")
    d.people.forEach(p => {
      const pill = document.createElement("span")
      pill.className = "rempill"
      pill.textContent = p
      const x = document.createElement("button")
      x.type = "button"; x.className = "fx"; x.setAttribute("aria-label", m.dataset.labelRemove.replace("%{email}", p))
      x.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 18 18 6M6 6l12 12"/></svg>`
      x.addEventListener("click", () => { d.people = d.people.filter(y => y !== p); this._paintReminders() })
      pill.appendChild(x)
      box.insertBefore(pill, input)
    })
    const on = Array.from(m.querySelectorAll("[data-rem-date]")).filter(cb => cb.checked && !cb.disabled)
    const n = on.length
    const prev = m.querySelector("[data-rem-prev]")
    prev.classList.toggle("hidden", n === 0)
    if (n) {
      const lead = m.querySelector(`[data-rem-leads] .fchip[data-lead='${d.lead}']`)?.dataset.text || ""
      prev.querySelector("[data-rem-prev-title]").textContent = m.dataset.labelPrevTitle.replace("%{date}", on[0].dataset.label).replace("%{project}", m.dataset.project)
      prev.querySelector("[data-rem-prev-when]").textContent = m.dataset.labelPrevWhen.replace("%{when}", on[0].dataset.nice).replace("%{lead}", lead.toLowerCase())
    }
    const save = m.querySelector("[data-rem-save]")
    const upd = m.dataset.on === "1"
    save.textContent = (upd ? (n === 1 ? m.dataset.labelUpdateOne : m.dataset.labelUpdateMany) : (n === 1 ? m.dataset.labelAddOne : m.dataset.labelAddMany)).replace(/\d+/, String(n))
    save.disabled = n === 0
  }

  async reminderSave() {
    const m = this.remindersModalTarget
    const dates = {}
    m.querySelectorAll("[data-rem-date]").forEach(cb => { dates[cb.dataset.remDate] = cb.checked && !cb.disabled ? "1" : "0" })
    const n = Object.values(dates).filter(v => v === "1").length
    if (!n) return
    const was = m.dataset.on === "1"
    const p = this._remDraft.people.length
    m.classList.add("hidden")
    try {
      const data = await this._json("PATCH", `${this.baseUrlValue}/reminders`, { dates, lead: this._remDraft.lead, people: this._remDraft.people })
      this._applyTop(data)
      this._showToast(was ? this._t("reminders_updated") : (p ? this._t(n === 1 ? "reminder_added_shared_one" : "reminders_added_shared", { count: n, people: p }) : this._t(n === 1 ? "reminder_added_one" : "reminders_added", { count: n })))
    } catch (err) {
      this._showToast(err.message, "error")
    }
  }

  reminderRemove() {
    const m = this.remindersModalTarget
    m.classList.add("hidden")
    this._json("PATCH", `${this.baseUrlValue}/reminders`, { remove: "1" })
      .then(data => { this._applyTop(data); this._showToast(this._t("reminders_removed")) })
      .catch(err => this._showToast(err.message, "error"))
  }

  // ── Panels + summary ──────────────────────────────────────────────────
  _applyResponse(data) {
    if (data.package_id) this._selectedPackage = String(data.package_id)
    if (data.panels) {
      Object.entries(data.panels).forEach(([step, html]) => {
        const panel = this.panelTargets.find(p => p.dataset.step === String(step))
        if (panel) { panel.innerHTML = html; panel.dataset.loaded = "1" }
      })
    }
    if (data.summary) this._applySummary(data.summary)
    if (data.stale) this._markStale(data.stale)
    if (this._codesView) { this._codes?.applyResponse(data); this._watchAiRuns(); return }
    this._watchAiRuns()
  }

  _applySummary(s) {
    if (this.hasBudgetTotalTarget) this.budgetTotalTarget.textContent = s.budget_total_long || s.budget_total
    if (this.hasPerSfTarget) this.perSfTarget.textContent = s.per_sf
    if (this.hasQuestionsCountTarget) {
      this.questionsCountTarget.textContent = s.questions_label || s.questions
      if (s.open_questions !== undefined) this.questionsCountTarget.classList.toggle("hot", s.open_questions > 0)
    }
    if (this.hasAddendaCountTarget) this.addendaCountTarget.textContent = s.addenda
    const carried = this.element.querySelector("[data-precon-target='carried']")
    if (carried && s.carried) carried.textContent = s.carried
    this.stepSubTargets.forEach(el => { const v = s.step_subs?.[el.dataset.step]; if (v !== undefined) el.textContent = v })
    if (s.step_done) this.stepButtonTargets.forEach(el => { el.dataset.done = s.step_done[el.dataset.step] ? "1" : "0" })
    if (s.step_alerts !== undefined) this._alerts = s.step_alerts || {}
    if (s.attention !== undefined) { this.attentionValue = s.attention || {}; this._renderAttention() }
    this._paintStepBar()
    const dline = this.element.querySelector("[data-precon-target='dline']")
    if (dline && s.bids_due_at !== undefined) { dline.dataset.due = s.bids_due_at || ""; this._tickCountdown() }
  }

  // The budget template keeps the view the person is on (package / division).
  get _viewQuery() {
    // By cost code: the open cost code, so the answer carries its rows (precon-codes).
    if (this._codesView && this._codes && !this._codes.inFull) return this._codes.query
    const parts = []
    const budget = this.element.querySelector("[data-precon-target='budget']")
    if (budget?.dataset.view) parts.push(`view=${budget.dataset.view}`)
    const group = this.element.querySelector("[data-precon-target='level'] .segbtn.on[data-group]")?.dataset.group
    if (group) parts.push(`group=${group}`)
    const tab = this.element.querySelector("[data-precon-target='invite'] .tab3.on")?.dataset.tab
    if (tab) parts.push(`tab=${tab}`)
    const detail = this.element.querySelector("[data-precon-target='paper']")?.dataset.detail
    if (detail) parts.push(`detail=${detail}`)
    if (this._selectedPackage) parts.push(`package_id=${this._selectedPackage}`)
    return parts.join("&")
  }

  async _act(method, path, body, okMessage) {
    try {
      const q = this._viewQuery
      const url = `${this.baseUrlValue}/${path}${q ? (path.includes("?") ? "&" : "?") + q : ""}`
      const data = await this._json(method, url, body)
      this._applyResponse(data)
      if (okMessage) this._showToast(okMessage)
      return data
    } catch (e) {
      this._showToast(e.message, "error")
      return null
    }
  }

  async refreshPanel(step) {
    if (this._codesView) return this._codes?.refresh(step)
    const q = this._viewQuery
    const data = await this._json("GET", `${this.baseUrlValue}/panel/${step}${q ? "?" + q : ""}`)
    this._applyResponse(data)
  }

  // ── AI runs (poll until done) ─────────────────────────────────────────
  _watchAiRuns() {
    const banner = this.element.querySelector("[data-precon-target='aiBanner'][data-run-id]")
    if (!banner) return
    const runId = banner.dataset.runId
    if (this._polling === runId) return
    this._polling = runId
    const poll = async () => {
      try {
        const q = this._viewQuery
        const data = await this._json("GET", `${this.baseUrlValue}/ai_runs/${runId}${q ? "?" + q : ""}`)
        if (data.status === "done") {
          this._polling = null
          this._applyResponse(data)
          const r = data.result || {}
          if (data.toast) this._showToast(data.toast)
          else if (r.added !== undefined) this._showToast(`AI read the plans: ${r.added} cost codes added, ${r.updated} updated, ${r.packages} bid packages.`)
          else if (r.packages !== undefined) this._showToast(`AI set up ${r.packages} bid packages, one per type of work.`)
          else if (r.picked !== undefined) this._showToast(`AI picked ${r.picked} bidders from your master sub list.`)
          return
        }
        // Step 1: where AI is (the banner's words and its progress bar).
        if (data.progress) {
          const b = this.element.querySelector(`[data-precon-target='aiBanner'][data-run-id='${runId}']`)
          const msg = b?.querySelector("[data-ai-msg]")
          if (msg && data.progress.text) msg.textContent = data.progress.text
          const bar = b?.querySelector("[data-ai-bar]")
          if (bar && data.progress.pct !== undefined) bar.style.width = `${data.progress.pct}%`
        }
        if (data.status === "failed") {
          this._polling = null
          this._showToast(data.error || "AI couldn't finish. Try again.", "error")
          await this.refreshPanel(this.stepValue)
          return
        }
      } catch (e) { /* keep polling */ }
      this._pollTimer = setTimeout(poll, 2500)
    }
    this._pollTimer = setTimeout(poll, 2500)
  }

  stopPropagation(e) { e.stopPropagation() }

  // By cost code view, step 1: the row's "Take it out of this job".
  removeCodeFromJob(e) {
    const id = e.currentTarget.dataset.costCodeId
    this._confirm("Take it out of this job?", "It leaves the budget template and its package. You can put it back from Edit the template.", "Take it out", () => this._act("PATCH", `cost_codes/${id}`, { active: "0" }, "Taken out."))
  }

  blurField(e) { e.preventDefault(); e.currentTarget.blur() }

  // ── Edit packages ─────────────────────────────────────────────────────
  openEditPackages() {
    const modal = this.element.querySelector("[data-precon-target='editPackagesModal']")
    if (modal) modal.classList.remove("hidden")
    track("Precon Edit Packages Opened", { project_id: this.projectIdValue })
  }
  closeEditPackages() {
    const modal = this.element.querySelector("[data-precon-target='editPackagesModal']")
    if (modal) modal.classList.add("hidden")
  }

  openQuestions() { if (this._codesView) { this._codes?.showFullStep(3, "qa"); return } this._s3Intent("qa") }
  openAddenda() { if (this._codesView) { this._codes?.showFullStep(3, "qa"); return } this._s3Intent("addenda") }

  // ── Step 3 (Invite and bid) ───────────────────────────────────────────
  // The top row's Questions / Addenda: step 3 opens on its Questions tab, or
  // with the Addenda pop-up. Step 3's own controller (precon-invite) takes
  // the intent when it connects (panel not loaded yet) or right away.
  _s3Intent(what) {
    this.element.dataset.s3Intent = what
    this.showStep(3, true)
    this.element.dispatchEvent(new CustomEvent("precon:s3-intent", { detail: { what } }))
  }

  // ── Step 2: Pick bidders ──────────────────────────────────────────────
  // Only the selected package's pane is on the page; picking another one loads it.
  selectPackage(e) {
    const id = e.currentTarget.dataset.packageId
    const panel = e.currentTarget.closest("[data-precon-target='panel']")
    const scope = panel || this.element
    this._selectedPackage = id
    scope.querySelectorAll("[data-precon-target='packageRow']").forEach(r => r.classList.toggle("sel", r.dataset.packageId === id))
    const pane = scope.querySelector(`[data-precon-target='packagePanel'][data-package-id='${id}']`)
    if (!pane && panel) { this.refreshPanel(parseInt(panel.dataset.step, 10)); return }
    scope.querySelectorAll("[data-precon-target='packagePanel']").forEach(p => p.classList.toggle("hidden", p.dataset.packageId !== id))
  }

  // From the tracker or the leveling page: open a step on one package.
  goToPackage(e) {
    const { step, packageId } = e.currentTarget.dataset
    if (this._codesView) { this._codes?.openPackageStep(packageId, step); return }
    this._selectedPackage = packageId
    const n = parseInt(step, 10)
    const panel = this.panelTargets.find(p => p.dataset.step === String(step))
    const pane = panel?.querySelector(`[data-precon-target='packagePanel'][data-package-id='${packageId}']`)
    if (panel && panel.dataset.loaded !== "0" && pane) {
      this.showStep(n, true)
      const row = panel.querySelector(`[data-precon-target='packageRow'][data-package-id='${packageId}']`)
      if (row) this.selectPackage({ currentTarget: row })
    } else {
      if (panel) panel.dataset.loaded = "1"
      this.showStep(n, true)
      this.refreshPanel(n)
    }
  }

  // ── By cost code view ─────────────────────────────────────────────────
  // Rows, panels, grouping and filters live in precon_codes_controller.js
  // (attached inside the page); this hands it the actions' answers.
  get _codes() {
    const el = this.element.querySelector("[data-controller~='precon-codes']")
    return el ? this.application.getControllerForElementAndIdentifier(el, "precon-codes") : null
  }

  // The View switch to By step keeps the place: { step, package_id } of the
  // open cost code (or the whole step shown), {} when nothing is open.
  _syncParams() { return this._codes?.syncParams() || {} }

  remindAll(e) {
    const ids = (e.currentTarget.dataset.ids || "").split(",").filter(Boolean)
    if (!ids.length) { this._showToast("Nobody is waiting."); return }
    this._confirm(`Remind ${ids.length} sub${ids.length === 1 ? "" : "s"}?`, "Everyone who hasn't bid or declined gets a reminder email with their link.", "Send reminders", async () => {
      for (const id of ids) await this._json("POST", `${this.baseUrlValue}/invites/${id}/remind`, {})
      await this.refreshPanel(3)
      this._showToast("Reminders sent.")
    })
  }

  remind(e) { this._act("POST", `invites/${e.currentTarget.dataset.bidderId}/remind`, {}, "Reminder sent.") }

  answerQuestion(e) {
    e.preventDefault()
    const form = e.currentTarget
    const publish = e.submitter?.value === "1" ? "1" : "0"
    const answer = form.answer.value.trim()
    if (!answer) { this._showToast("Type the answer first.", "error"); return }
    this._act("PATCH", `questions/${form.dataset.questionId}`, { answer, publish }, publish === "1" ? "Answer published to the bidders." : "Answer saved.")
  }

  _esc(s) { return String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])) }

  // ── Shared: confirm modal, toast, JSON ────────────────────────────────
  // The prototype's confirm pop-up (title, message, Cancel / the action),
  // inside the page so its styles and dark mode apply. Never window.confirm.
  _confirm(title, message, action, onYes, onNo) {
    const wrap = document.createElement("div")
    wrap.className = "overlay"
    wrap.innerHTML = `
      <div class="modal" role="dialog" data-panel>
        <div class="mhead"><h2 class="cardtitle"></h2><button class="x" type="button" data-x aria-label="${this._esc(this._t("close"))}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 18 18 6M6 6l12 12"/></svg></button></div>
        <div class="pad col12"><span data-msg></span></div>
        <div class="mfoot">
          <button type="button" class="btn btn-secondary" data-no></button>
          <button type="button" class="btn btn-primary" data-yes></button>
        </div>
      </div>`
    wrap.setAttribute("aria-label", title)
    wrap.querySelector("h2").textContent = title
    wrap.querySelector("[data-msg]").textContent = message
    wrap.querySelector("[data-no]").textContent = this._t("cancel")
    wrap.querySelector("[data-yes]").textContent = action
    const close = () => { wrap.remove(); this._openConfirm = null }
    const no = () => { close(); if (onNo) onNo() }
    wrap.addEventListener("click", (e) => { if (e.target === wrap) no() })
    wrap.querySelector("[data-no]").addEventListener("click", no)
    wrap.querySelector("[data-x]").addEventListener("click", no)
    wrap.querySelector("[data-yes]").addEventListener("click", () => { close(); onYes() })
    this.element.appendChild(wrap)
    wrap.querySelector("[data-yes]").focus()
    this._openConfirm = { close, onNo }
  }

  _closeOpenModals() {
    if (this._openConfirm) { const c = this._openConfirm; c.close(); if (c.onNo) c.onNo(); return }
    this.element.querySelectorAll(".overlay:not(.hidden)").forEach(o => { if (o.dataset.preconTarget) o.classList.add("hidden") })
    this.closeEditPackages()
    this.element.querySelectorAll("[data-precon-target='modal']").forEach(m => m.classList.add("hidden"))
  }

  _showToast(message, type = "notice") {
    const toast = document.createElement("div")
    toast.className = type === "error" ? "flash-toast-alert" : "flash-toast-notice"
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.textContent = message
    document.body.prepend(toast)
  }

  async _json(method, url, body) {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token },
      body: body === undefined ? undefined : JSON.stringify(body)
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok || data.ok === false) throw new Error(data.error || "Something went wrong")
    return data
  }
}
